import { Fragment, type JSX, type MouseEvent, type ReactNode } from 'react';
import { Loader2, Music2, Pause, Play } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSounds } from '../hooks/sounds';
import { soundUrl, type SoundPlatform } from '../lib/platform';
import { soundKey, splitSoundMentions, type SoundRef } from '../lib/sounds';
import { useSoundPlayer } from '../lib/soundPlayer';

/**
 * Every sound the UI shows goes through here: its cover, its name (or
 * "Sound <id>" until the catalogue knows it) linking to the sound's page, and
 * for TikTok a ▶ that plays the preview in place through the one shared
 * player. Instagram audio is a link only — there is no preview to play.
 *
 * Metadata the caller already has (a resolved paste, a lookalike analysis)
 * is passed in and used first; anything missing comes from the catalogue via
 * `useSounds`, batched across every sound on the page. A server without the
 * catalogue leaves id-only links, never a broken page.
 */

interface SoundMeta {
  title?: string | null;
  author?: string | null;
  coverUrl?: string | null;
  playUrl?: string | null;
}

export interface SoundLinkProps extends SoundMeta {
  platform: SoundPlatform | string | null | undefined;
  id: string;
  /** `inline` sits in a sentence; `row` is the larger chip with author and id under the name. */
  variant?: 'inline' | 'row';
  /** Inline only: add "· author" after the name. */
  showAuthor?: boolean;
  className?: string;
}

const toSoundPlatform = (p: unknown): SoundPlatform => (p === 'instagram' ? 'instagram' : 'tiktok');
export const soundFallbackName = (id: string): string => `Sound ${id}`;

/** Caller's metadata first, then the catalogue's. */
function useSoundMeta(platform: SoundPlatform, id: string, given: SoundMeta) {
  // Nothing to ask when the caller already knows everything we'd show.
  const complete = !!given.title && (platform === 'instagram' || !!given.playUrl);
  const { byKey } = useSounds(complete ? NONE : [{ platform, id }]);
  const info = byKey.get(soundKey({ platform, id }));
  return {
    title: given.title || info?.title || null,
    author: given.author || info?.author || null,
    coverUrl: given.coverUrl || info?.coverUrl || null,
    playUrl: platform === 'tiktok' ? given.playUrl || info?.playUrl || null : null,
  };
}
const NONE: SoundRef[] = [];

/** Clicks on a sound must not also trigger the row, card or menu item it sits in. */
const stop = (ev: MouseEvent) => ev.stopPropagation();

/**
 * Inline sounds are laid out as text (inline boxes, not one inline-flex box):
 * inside a truncated line, an atomic box that doesn't fit is replaced whole
 * by the ellipsis, so "Sounds: Espresso" read "Sounds: …". As text, the name
 * itself is what gets cut.
 */
const INLINE_ICON = 'mr-1 inline-block align-middle';

function Cover({ src, size }: { src: string | null; size: 'sm' | 'lg' }): JSX.Element {
  const box = size === 'lg' ? 'h-8 w-8 shrink-0 rounded-md' : cn(INLINE_ICON, 'h-4 w-4 rounded-sm');
  if (!src) {
    return <Music2 className={size === 'lg' ? 'mx-2 h-4 w-4 shrink-0 text-muted-foreground' : cn(INLINE_ICON, 'h-3.5 w-3.5 text-muted-foreground')} aria-hidden />;
  }
  return (
    <img
      src={src}
      alt=""
      className={cn('object-cover', box)}
      // An expired cover must not leave a broken-image icon behind.
      onError={(ev) => {
        ev.currentTarget.style.display = 'none';
      }}
    />
  );
}

/** ▶ / ❚❚ for one sound in the shared player. */
export function QuickListen({
  soundId,
  playUrl,
  name,
  size = 'sm',
}: {
  soundId: string;
  playUrl: string;
  name: string;
  size?: 'sm' | 'lg';
}): JSX.Element {
  const { status, toggle } = useSoundPlayer(soundKey({ platform: 'tiktok', id: soundId }));
  const playing = status === 'playing';
  const loading = status === 'loading';
  const Icon = loading ? Loader2 : playing ? Pause : Play;
  return (
    <button
      type="button"
      onClick={(ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        void toggle(playUrl);
      }}
      aria-label={playing || loading ? `Pause ${name}` : `Play ${name}`}
      aria-pressed={playing || loading}
      data-sound-player={status}
      title={status === 'error' ? 'Preview unavailable — try again or open it on TikTok' : playing ? 'Pause' : 'Quick listen'}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full border transition-colors',
        size === 'lg' ? 'h-7 w-7' : 'mr-1 h-5 w-5 align-middle',
        playing || loading ? 'border-brand/50 bg-brand/15 text-brand' : 'border-border bg-background hover:bg-accent',
        status === 'error' && 'border-destructive/40 text-destructive-strong',
      )}
    >
      <Icon className={cn(size === 'lg' ? 'h-3.5 w-3.5' : 'h-2.5 w-2.5', loading && 'animate-spin', !playing && !loading && 'fill-current')} />
    </button>
  );
}

export function SoundLink({
  platform: rawPlatform,
  id,
  variant = 'inline',
  showAuthor = false,
  className,
  ...given
}: SoundLinkProps): JSX.Element {
  const platform = toSoundPlatform(rawPlatform);
  const meta = useSoundMeta(platform, id, given);
  const name = meta.title ?? soundFallbackName(id);
  const href = soundUrl(platform, id);
  const where = platform === 'instagram' ? 'Instagram' : 'TikTok';

  if (variant === 'row') {
    return (
      <span className={cn('flex min-w-0 flex-1 items-center gap-2', className)} data-sound={id}>
        <Cover src={meta.coverUrl} size="lg" />
        {meta.playUrl && <QuickListen soundId={id} playUrl={meta.playUrl} name={name} size="lg" />}
        <a href={href} target="_blank" rel="noreferrer" onClick={stop} className="group min-w-0 flex-1" title={`Open this sound on ${where}`}>
          <span className="block truncate text-sm font-medium group-hover:underline">{name}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {meta.author ? `${meta.author} · ` : ''}
            {id}
          </span>
        </a>
      </span>
    );
  }

  return (
    <span className={className} data-sound={id}>
      <Cover src={meta.coverUrl} size="sm" />
      {meta.playUrl && <QuickListen soundId={id} playUrl={meta.playUrl} name={name} />}
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        onClick={stop}
        className="underline-offset-2 hover:underline"
        title={`${name}${meta.author ? ` · ${meta.author}` : ''} — open on ${where}`}
      >
        {name}
      </a>
      {showAuthor && meta.author && <span className="text-muted-foreground"> · {meta.author}</span>}
    </span>
  );
}

/**
 * The sound's name and cover with nothing to click — for places that are
 * already one big button (a saved search, a history row), where a link inside
 * would be invalid HTML and steal the click.
 */
export function SoundName({ platform: rawPlatform, id, className, ...given }: SoundLinkProps): JSX.Element {
  const platform = toSoundPlatform(rawPlatform);
  const meta = useSoundMeta(platform, id, given);
  return (
    <span className={className} data-sound={id}>
      <Cover src={meta.coverUrl} size="sm" />
      {meta.title ?? soundFallbackName(id)}
    </span>
  );
}

/** Several sounds in a sentence: "A, B and 3 more". */
export function SoundList({
  sounds,
  linked = true,
  max = 6,
  separator = ', ',
}: {
  sounds: readonly SoundRef[];
  linked?: boolean;
  max?: number;
  separator?: ReactNode;
}): JSX.Element {
  const shown = sounds.slice(0, max);
  const Item = linked ? SoundLink : SoundName;
  return (
    <>
      {shown.map((s, i) => (
        <Fragment key={soundKey(s)}>
          {i > 0 && separator}
          <Item platform={s.platform} id={s.id} />
        </Fragment>
      ))}
      {sounds.length > max && ` and ${sounds.length - max} more`}
    </>
  );
}

/** Server text with every "sound <id>" / "audio:<id>" in it turned into a sound link. */
export function SoundText({ text }: { text: string }): JSX.Element {
  const parts = splitSoundMentions(text);
  if (!parts.some((p) => 'sound' in p)) return <>{text}</>;
  return (
    <>
      {parts.map((p, i) =>
        'sound' in p ? (
          // Keep the server's word ("sound" / "audio") so the sentence still reads.
          <Fragment key={i}>
            {/^\w+/.exec(p.text)?.[0]} <SoundLink platform={p.sound.platform} id={p.sound.id} />
          </Fragment>
        ) : (
          <Fragment key={i}>{p.text}</Fragment>
        ),
      )}
    </>
  );
}
